import type { z } from 'zod'
import type { AgentV2LivingAssetReferenceSchema, AgentV2Reference } from '@/agent-v2/store/schemas'
import { formatAdministrativeDecisionMetadataLine, formatAdministrativeDecisionTitle } from '@/legal-sources/utils/administrative-decision-formatting'
import { formatCaseLawBluebookTitle, formatCaseLawMetadataLine } from '@/legal-sources/utils/case-law-formatting'
import { formatLrrMetadataLine, formatLrrTitle } from '@/legal-sources/utils/lrr-formatting'

type AgentV2LivingAssetKind = z.infer<typeof AgentV2LivingAssetReferenceSchema>['source']['asset_kind']

/**
 * Display labels for living-asset kinds. Keyed by the schema's full enum so a new
 * kind fails the typecheck instead of silently inheriting another kind's label.
 */
export const kAgentV2LivingAssetKindLabels: Record<AgentV2LivingAssetKind, string> = {
  med_chron: 'Medical chronology',
  timeline: 'Timeline',
  med_summary: 'Medical billing summary',
}

/** The muted qualifier shown after a court docket's name, marking it as a live record rather than legal authority. */
export const kAgentV2DocketQualifierLabel = 'Court docket'

/** The source document's display name for a references-section row, per source family. */
export function getAgentV2ReferenceDisplayName(reference: AgentV2Reference): string {
  switch (reference.source_type) {
    case 'web':
    case 'docket':
      return reference.source.title
    case 'caselaw':
      return formatCaseLawBluebookTitle(reference.source)
    case 'lrr':
      return formatLrrTitle(reference.source)
    case 'administrative_decision':
      return formatAdministrativeDecisionTitle(reference.source)

    case 'matter_file':
    case 'living_asset':
      return reference.source.filename
  }
}

/**
 * One-line metadata summary for a reference row:
 * - caselaw: jurisdiction code, decide year, and docket
 * - lrr: law type (capitalized) and jurisdiction code
 * - administrative_decision: readable document type, full source name, and publication date
 * - matter_file: chunk page numbers, shown as-is (they arrive one-based)
 * - living_asset: source page numbers (stored zero-based; display adds 1)
 * - docket: court (when known) and retrieval date
 */
export function getAgentV2ReferenceMetadataSummaryText(reference: AgentV2Reference): string | null {
  switch (reference.source_type) {
    case 'web':
      return `${new URL(reference.source.url).hostname} • Retrieved ${reference.source.retrieved_at.slice(0, 10)}`
    case 'docket': {
      const retrieved = `Retrieved ${reference.source.retrieved_at.slice(0, 10)}`
      return reference.source.court ? `${reference.source.court} • ${retrieved}` : retrieved
    }
    case 'caselaw':
      return formatCaseLawMetadataLine(reference.source)
    case 'lrr':
      return formatLrrMetadataLine(reference.source)
    case 'administrative_decision':
      return formatAdministrativeDecisionMetadataLine(reference.source)

    case 'matter_file':
      return formatPages(reference.source.page_nums)

    case 'living_asset':
      return formatPages(reference.source.page_nums.map((pageNum) => pageNum + 1))
  }
}

function formatPages(pageNums: number[]): string | null {
  if (pageNums.length === 0) return null
  if (pageNums.length === 1) return `Page ${pageNums[0]}`
  return `Pages ${pageNums.join(', ')}`
}
